import React, {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { Icon } from '../atoms/Icon'

export interface InfoTooltipProps {
  children: ReactNode
  className?: string
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right'
  width?: string
  /** Article slug to link to help system */
  helpArticle?: string
  /** Callback when help link is clicked */
  onHelpClick?: (articleSlug: string) => void
  /** Label for the help link */
  helpLinkLabel?: string
}

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;

  .icon {
    color: ${({ theme }) => theme.colors.text.tertiary};
    transition: color 0.15s ease;
  }

  &:hover .icon {
    color: ${({ theme }) => theme.colors.primary};
  }
`

type ActualPlacement = 'top' | 'bottom' | 'left' | 'right'

const arrowStyles = {
  top: css`
    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 6px solid transparent;
      border-top-color: ${({ theme }) => theme.colors.background.primary};
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
    }
    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 7px solid transparent;
      border-top-color: ${({ theme }) => theme.colors.border.default};
      bottom: -14px;
      left: 50%;
      transform: translateX(-50%);
    }
  `,
  bottom: css`
    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 6px solid transparent;
      border-bottom-color: ${({ theme }) => theme.colors.background.primary};
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
    }
    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 7px solid transparent;
      border-bottom-color: ${({ theme }) => theme.colors.border.default};
      top: -14px;
      left: 50%;
      transform: translateX(-50%);
    }
  `,
  left: css`
    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 6px solid transparent;
      border-left-color: ${({ theme }) => theme.colors.background.primary};
      right: -12px;
      top: 50%;
      transform: translateY(-50%);
    }
    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 7px solid transparent;
      border-left-color: ${({ theme }) => theme.colors.border.default};
      right: -14px;
      top: 50%;
      transform: translateY(-50%);
    }
  `,
  right: css`
    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 6px solid transparent;
      border-right-color: ${({ theme }) => theme.colors.background.primary};
      left: -12px;
      top: 50%;
      transform: translateY(-50%);
    }
    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border: 7px solid transparent;
      border-right-color: ${({ theme }) => theme.colors.border.default};
      left: -14px;
      top: 50%;
      transform: translateY(-50%);
    }
  `,
}

const TooltipPortalContent = styled.div<{
  $visible: boolean
  $width: string
  $x: number
  $y: number
  $actualPlacement: ActualPlacement
}>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  z-index: 10000000;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 10px;
  padding: 11px 12px;
  width: ${({ $width }) => $width};
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.07),
    0 10px 15px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.02);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.15s ease,
    visibility 0.15s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  /* Apply the appropriate arrow based on actual placement */
  ${({ $actualPlacement }) => arrowStyles[$actualPlacement]}

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 11px;
  }

  .warning {
    display: block;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${({ theme }) => theme.colors.border.light};
    color: ${({ theme }) => theme.colors.warning};
    font-size: 10px;
    line-height: 1.5;

    &::before {
      content: '⚠️';
      margin-right: 5px;
      font-size: 12px;
    }
  }

  /* Ensure bullet points are properly styled */
  br + • {
    display: inline-block;
    margin-left: 0;
  }
`

const HelpLink = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 8px;
  padding-top: 8px;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
  width: 100%;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }

  .help-icon {
    flex-shrink: 0;
  }
`

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  children,
  className,
  placement = 'auto',
  width = '304px',
  helpArticle,
  onHelpClick,
  helpLinkLabel = 'Learn more',
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [actualPlacement, setActualPlacement] = useState<ActualPlacement>('top')
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipDimensions, setTooltipDimensions] = useState({
    width: 304,
    height: 100,
  })

  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) return

    const iconRect = containerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const spacing = 8 // Space between icon and tooltip
    const padding = 12 // Minimum distance from viewport edges

    // Get actual dimensions
    const tooltipWidth = tooltipRect.width || tooltipDimensions.width
    const tooltipHeight = tooltipRect.height || tooltipDimensions.height

    // Update dimensions for next calculation
    if (tooltipRect.width && tooltipRect.height) {
      setTooltipDimensions({
        width: tooltipRect.width,
        height: tooltipRect.height,
      })
    }

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const scrollX = window.scrollX
    const scrollY = window.scrollY

    // Calculate available space in each direction
    const spaceAbove = iconRect.top - scrollY
    const spaceBelow = viewportHeight - (iconRect.bottom - scrollY)
    const spaceLeft = iconRect.left - scrollX
    const spaceRight = viewportWidth - (iconRect.right - scrollX)

    // Determine best placement
    let bestPlacement: ActualPlacement = 'top'

    if (placement === 'auto') {
      // Smart positioning: choose the direction with most space
      const positions = [
        {
          placement: 'top' as const,
          space: spaceAbove,
          needed: tooltipHeight + spacing + padding,
        },
        {
          placement: 'bottom' as const,
          space: spaceBelow,
          needed: tooltipHeight + spacing + padding,
        },
        {
          placement: 'left' as const,
          space: spaceLeft,
          needed: tooltipWidth + spacing + padding,
        },
        {
          placement: 'right' as const,
          space: spaceRight,
          needed: tooltipWidth + spacing + padding,
        },
      ]

      // Sort by available space and pick the best one
      positions.sort((a, b) => {
        // Prefer vertical positions if there's enough space
        const aFits = a.space >= a.needed
        const bFits = b.space >= b.needed

        if (aFits && !bFits) return -1
        if (!aFits && bFits) return 1

        // If both fit or both don't fit, prefer top/bottom over left/right
        if (
          (a.placement === 'top' || a.placement === 'bottom') &&
          (b.placement === 'left' || b.placement === 'right')
        ) {
          return -1
        }
        if (
          (b.placement === 'top' || b.placement === 'bottom') &&
          (a.placement === 'left' || a.placement === 'right')
        ) {
          return 1
        }

        return b.space - a.space
      })

      bestPlacement = positions[0].placement
    } else {
      bestPlacement = placement
    }

    // Calculate position based on placement
    let x = 0
    let y = 0

    switch (bestPlacement) {
      case 'top':
        x = iconRect.left + iconRect.width / 2 - tooltipWidth / 2
        y = iconRect.top - tooltipHeight - spacing
        // Adjust if tooltip goes off screen horizontally
        if (x < padding) x = padding
        if (x + tooltipWidth > viewportWidth - padding) {
          x = viewportWidth - tooltipWidth - padding
        }
        // If not enough space above, flip to bottom
        if (y < padding) {
          bestPlacement = 'bottom'
          y = iconRect.bottom + spacing
        }
        break

      case 'bottom':
        x = iconRect.left + iconRect.width / 2 - tooltipWidth / 2
        y = iconRect.bottom + spacing
        // Adjust if tooltip goes off screen horizontally
        if (x < padding) x = padding
        if (x + tooltipWidth > viewportWidth - padding) {
          x = viewportWidth - tooltipWidth - padding
        }
        // If not enough space below, flip to top
        if (y + tooltipHeight > viewportHeight - padding) {
          bestPlacement = 'top'
          y = iconRect.top - tooltipHeight - spacing
        }
        break

      case 'left':
        x = iconRect.left - tooltipWidth - spacing
        y = iconRect.top + iconRect.height / 2 - tooltipHeight / 2
        // Adjust if tooltip goes off screen vertically
        if (y < padding) y = padding
        if (y + tooltipHeight > viewportHeight - padding) {
          y = viewportHeight - tooltipHeight - padding
        }
        // If not enough space on left, flip to right
        if (x < padding) {
          bestPlacement = 'right'
          x = iconRect.right + spacing
        }
        break

      case 'right':
        x = iconRect.right + spacing
        y = iconRect.top + iconRect.height / 2 - tooltipHeight / 2
        // Adjust if tooltip goes off screen vertically
        if (y < padding) y = padding
        if (y + tooltipHeight > viewportHeight - padding) {
          y = viewportHeight - tooltipHeight - padding
        }
        // If not enough space on right, flip to left
        if (x + tooltipWidth > viewportWidth - padding) {
          bestPlacement = 'left'
          x = iconRect.left - tooltipWidth - spacing
        }
        break
    }

    setPosition({ x, y })
    setActualPlacement(bestPlacement)
  }, [placement, tooltipDimensions])

  useEffect(() => {
    if (isVisible) {
      // Initial calculation
      calculatePosition()

      // Recalculate after a short delay to account for content loading
      const timeoutId = setTimeout(calculatePosition, 10)

      // Update on scroll and resize
      const handleUpdate = () => calculatePosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)

      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }
  }, [isVisible])

  const handleMouseEnter = () => setIsVisible(true)
  const handleMouseLeave = () => setIsVisible(false)

  return (
    <>
      <TooltipContainer
        ref={containerRef}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Icon name="info" size="small" className="icon" />
      </TooltipContainer>
      {typeof document !== 'undefined' &&
        createPortal(
          <TooltipPortalContent
            ref={tooltipRef}
            $visible={isVisible}
            $width={width}
            $x={position.x}
            $y={position.y}
            $actualPlacement={actualPlacement}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {children}
            {helpArticle && onHelpClick && (
              <HelpLink
                onClick={(e) => {
                  e.stopPropagation()
                  onHelpClick(helpArticle)
                  setIsVisible(false)
                }}
              >
                <Icon name="helpCircle" size="small" className="help-icon" />
                {helpLinkLabel}
              </HelpLink>
            )}
          </TooltipPortalContent>,
          document.body
        )}
    </>
  )
}
